import { Text } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { useCallback, useRef, useState } from 'react';

import { BulkModalActionType } from 'app/components/appModals/components/conversationsBulkModal';
import {
  buildCopilotActions,
  copilotActionContexts,
} from 'components/copilotActions';
import { CopilotPinnedActions } from 'components/copilotPinnedActions';
import { EditorCopilotDropdown } from 'components/editorCopilotDropdown';
import { EditorMentions } from 'components/editorMentions';
import { EditorQuickRepliesDropdown } from 'components/editorQuickRepliesDropdown';
// eslint-disable-next-line no-restricted-imports
import RichTextField, {
  RichTextFieldRef,
} from 'components/shared/util/richTextField/components/richTextField';

import styles from './conversationsBulkTextEditor.module.scss';

type ConversationsBulkTextEditorProps = {
  type: Extract<BulkModalActionType, 'send_reply' | 'add_internal_note'>;
  placeholder?: string;
  /**
   * The maximum number of characters allowed. When set, a character counter is
   * shown and the count turns red once the limit is exceeded.
   */
  maxTextLength?: number | null;
  onChange: (html: string, plain: string) => void;
};

export const ConversationsBulkTextEditor = ({
  placeholder,
  type,
  maxTextLength,
  onChange,
}: ConversationsBulkTextEditorProps): JSX.Element => {
  const editorRef = useRef<RichTextFieldRef | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [plainTextLength, setPlainTextLength] = useState(0);

  const handleChange = useCallback(() => {
    const plainText = editorRef.current?.getPlainText() || '';
    setPlainTextLength(plainText.length);
    onChange(editorRef.current?.getText() || '', plainText);
  }, [onChange]);

  const hasMaxTextLength =
    typeof maxTextLength === 'number' && maxTextLength > 0;
  const isOverMaxTextLength =
    hasMaxTextLength && plainTextLength > maxTextLength;

  const copilotContext =
    copilotActionContexts[
      type === 'add_internal_note' ? 'bulkNote' : 'bulkMessage'
    ];
  const editorCopilotActions = buildCopilotActions(copilotContext, {});

  return (
    <>
      <div
        className={classnames(styles.container, {
          [styles.containerInternalNote]: type === 'add_internal_note',
        })}
        ref={containerRef}
      >
        <RichTextField
          ref={editorRef}
          channel="bulk"
          placeholder={
            placeholder ??
            (type === 'add_internal_note'
              ? 'Type your internal note...'
              : 'Type your message...')
          }
          onChange={handleChange}
          toolbarAddons={
            <>
              {type === 'add_internal_note' && (
                <EditorMentions container={containerRef} />
              )}
              <EditorQuickRepliesDropdown
                popoverContent={{ side: 'bottom', align: 'start' }}
              />
              <EditorCopilotDropdown
                editorCopilotActions={editorCopilotActions}
              />
              <CopilotPinnedActions
                context={copilotContext}
                text={editorRef.current?.getText() || ''}
              />
            </>
          }
          className={styles.editor}
        />
      </div>
      {hasMaxTextLength && (
        <div className={styles.charCountRow}>
          {isOverMaxTextLength && (
            <Text.Paragraph variant="4" className={styles.charCountError}>
              Your reply exceeds the {maxTextLength} character limit supported
              by some selected channels.
            </Text.Paragraph>
          )}
          <Text.Paragraph
            variant="4"
            className={classnames(styles.charCount, {
              [styles.charCountError]: isOverMaxTextLength,
            })}
          >
            {plainTextLength} / {maxTextLength}
          </Text.Paragraph>
        </div>
      )}
    </>
  );
};
